import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_URL, LOCATION_ID } from "..";
import MasterLayout from "../masterLayout/MasterLayout";
import { Icon } from "@iconify/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

export default function ComparisonPage() {
  const today = new Date();
  const firstDayCurrent = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstDayPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastDayPrev = new Date(today.getFullYear(), today.getMonth(), 0);

  const formatDate = (date) => date.toISOString().split("T")[0];

  // Default: compare previous vs current month
  const [fromDate1, setFromDate1] = useState(formatDate(firstDayPrev));
  const [toDate1, setToDate1] = useState(formatDate(lastDayPrev));
  const [fromDate2, setFromDate2] = useState(formatDate(firstDayCurrent));
  const [toDate2, setToDate2] = useState(formatDate(today));

  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchComparison = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/payments/comparison`, {
        params: {
          fromDate1,
          toDate1,
          fromDate2,
          toDate2,
          locationId: LOCATION_ID,
        },
      });
      setComparisonData(res.data);
    } catch (err) {
      console.error("Comparison fetch error:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComparison();
  }, []);

  // Transform for chart
  const chartData = comparisonData
    ? [
        {
          name: "Income",
          Period1: comparisonData.period1.totalIncome,
          Period2: comparisonData.period2.totalIncome,
        },
        {
          name: "Balance",
          Period1: comparisonData.period1.remainingBalance,
          Period2: comparisonData.period2.remainingBalance,
        },
        {
          name: "Invoices",
          Period1: comparisonData.period1.totalInvoices,
          Period2: comparisonData.period2.totalInvoices,
        },
        {
          name: "Paid Invoices",
          Period1: comparisonData.period1.paidInvoices,
          Period2: comparisonData.period2.paidInvoices,
        },
        {
          name: "Unpaid Invoices",
          Period1: comparisonData.period1.unpaidInvoices,
          Period2: comparisonData.period2.unpaidInvoices,
        },
      ]
    : [];

  return (
    <MasterLayout>
      <div className="container py-5">
        <h3 className="mb-4">Monthly Comparison</h3>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-md-3">
            <label>From Date (Period 1)</label>
            <input
              type="date"
              value={fromDate1}
              onChange={(e) => setFromDate1(e.target.value)}
              className="form-control"
            />
          </div>
          <div className="col-md-3">
            <label>To Date (Period 1)</label>
            <input
              type="date"
              value={toDate1}
              onChange={(e) => setToDate1(e.target.value)}
              className="form-control"
            />
          </div>
          <div className="col-md-3">
            <label>From Date (Period 2)</label>
            <input
              type="date"
              value={fromDate2}
              onChange={(e) => setFromDate2(e.target.value)}
              className="form-control"
            />
          </div>
          <div className="col-md-3">
            <label>To Date (Period 2)</label>
            <input
              type="date"
              value={toDate2}
              onChange={(e) => setToDate2(e.target.value)}
              className="form-control"
            />
          </div>
          <div className="col-12 mt-3">
            <button className="btn btn-primary" onClick={fetchComparison}>
              Compare
            </button>
          </div>
        </div>

        {loading ? (
          <p>Loading comparison...</p>
        ) : comparisonData ? (
          <>
            {/* Infographic cards */}
            <div className="row text-center mb-4">
              {[
                { label: "Total Income", icon: "mdi:currency-usd", key: "totalIncome", color: "success" },
                { label: "Balance", icon: "mdi:wallet-outline", key: "remainingBalance", color: "warning" },
                { label: "Total Invoices", icon: "mdi:file-document", key: "totalInvoices", color: "primary" },
                { label: "Paid Invoices", icon: "mdi:check-circle", key: "paidInvoices", color: "success" },
                { label: "Unpaid Invoices", icon: "mdi:close-circle", key: "unpaidInvoices", color: "danger" },
              ].map((stat, idx) => (
                <div key={idx} className="col-md-4 mb-3">
                  <div className={`card shadow-sm border-0 text-${stat.color}`}>
                    <div className="card-body">
                      <Icon icon={stat.icon} width={30} className="mb-2" />
                      <h6>{stat.label}</h6>
                      <div className="d-flex justify-content-between">
                        <span>Period 1: {comparisonData.period1[stat.key]}</span>
                        <span>Period 2: {comparisonData.period2[stat.key]}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Graph */}
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="mb-3">Comparison Chart</h5>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Period1" fill="#8884d8" name="Period 1" />
                    <Bar dataKey="Period2" fill="#82ca9d" name="Period 2" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        ) : (
          <p>No comparison data found.</p>
        )}
      </div>
    </MasterLayout>
  );
}
